import React from 'react';
import { motion } from 'framer-motion';
import { useNavigate } from 'react-router-dom'; // Add this import for React Router
import { 
  Sun, 
  Zap, 
  Car, 
  Train, 
  Shield, 
  Battery,
  Box,
  Lightbulb,
  Settings,
  TrendingUp
} from 'lucide-react';

import engineer from "../assests/Frame 28.png"
import diagram from "../assests/diagram.png"

const PowerSystemsLanding = () => {
  const navigate = useNavigate(); // Initialize navigate hook

  const stats = [
    { number: "35+", label: "Years Experience" },
    { number: "100+", label: "Projects Delivered" },
    { number: "250+", label: "Team Members" },
    { number: "20+", label: "Countries Served" }
  ];

  const sectors = [
    { icon: Sun, label: "Solar" },
    { icon: Settings, label: "Motor" },
    { icon: Zap, label: "Power" },
    { icon: Train, label: "Railways" },
    { icon: Car, label: "EV" },
    { icon: Shield, label: "Defence" }
  ];

  const achievements = [
    {
      icon: Box,
      title: "Made in India for the world",
      description: "We have manufactured and supplied reliable, customised Power Electronics to over 26 countries"
    },
    {
      icon: Sun,
      title: "800+ MW of Solar Inverters",
      description: "We make sturdy, stylish & powerful Storage-Based MPPT Solar Inverters of 1kVA to 250kVA"
    },
    {
      icon: Settings,
      title: "Powering Critical Industries",
      description: "Now manufacturing Defence-grade AC DC Megawatt Rectifiers for Hydrogen Electrolysis"
    },
    {
      icon: TrendingUp,
      title: "Elevating India's Power Grid",
      description: "We build products for Power Generation, Transmission & Distribution- 30000+ working sites"
    }
  ];

  const containerVariants = {
    hidden: { opacity: 0 },
    visible: {
      opacity: 1,
      transition: {
        staggerChildren: 0.1
      }
    }
  };

  const itemVariants = {
    hidden: { y: 20, opacity: 0 },
    visible: {
      y: 0,
      opacity: 1,
      transition: {
        duration: 0.6
      }
    }
  };

  const sectorVariants = {
    hidden: { scale: 0, opacity: 0 },
    visible: {
      scale: 1,
      opacity: 1,
      transition: {
        type: "spring",
        stiffness: 200,
        damping: 15
      }
    }
  };

  // Navigation handler function
  const handleOrderNowClick = () => {
    navigate('/contact-us');
  };

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Stats Section */}
      <motion.div 
        className="bg-white py-4 px-4"
        variants={containerVariants}
        initial="hidden"
        animate="visible"
      >
        <div className="max-w-6xl mx-auto">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-8">
            {stats.map((stat, index) => (
              <motion.div 
                key={index}
                className="text-center"
                variants={itemVariants}
              >
                <div className="text-3xl md:text-4xl font-bold text-[#0572AF] mb-2">
                  {stat.number}
                </div>
                <div className="text-gray-600 text-sm md:text-base">
                  {stat.label}
                </div>
              </motion.div>
            ))}
          </div>
        </div>
      </motion.div>

      {/* About Section */}
      <motion.div 
        className="py-16 px-4"
        variants={containerVariants}
        initial="hidden"
        whileInView="visible"
        viewport={{ once: true }}
      >
        <div className="max-w-6xl mx-auto">
          <div className="grid lg:grid-cols-2 gap-12 items-center">
            <motion.div variants={itemVariants}>
              <div className="text-[#0572AF] text-2xl font-semibold mb-4">ABOUT US</div>
              <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-6">
                Powering Innovation{' '}
                <span className="text-[#0572AF]">Since 1985</span>
              </h2>
              <p className="text-gray-600 leading-relaxed">
                Vidyut Systems brings 35 years of R&D and manufacturing 
                expertise in power electronics to deliver ready-to-use power 
                cards through an equitable alliance. Our model lets you focus on 
                marketing and customer care while we handle the technical 
                development—so you can build your own brand equity without 
                stretching your resources.
              </p>
            </motion.div>
            
            <motion.div 
              className="relative"
              variants={itemVariants}
            >
              {/* Replace this div with your image */}
              <div className=" rounded-lg h-80 flex items-center justify-center">
                
                <img src={engineer} alt="Engineer working" className="rounded-lg w-full h-full object-cover" />
              </div>
            </motion.div>
          </div>
        </div>
      </motion.div>

      {/* Sectors Diagram */}
     
      <motion.div 
        className="py-16 px-4 bg-[#F4FBFF]"
        variants={containerVariants}
        initial="hidden"
        whileInView="visible"
        viewport={{ once: true }}
      >
           <h1 className="text-3xl md:text-4xl font-bold text-center text-gray-900 mb-6">Our solutions</h1>
        <div className="max-w-4xl mx-auto">
          
          <div className="relative flex items-center justify-center">
          
            {/* Center Circle */}
           <img src={diagram} alt="Engineer working" className="rounded-lg w-full h-full object-cover" />
          </div>
        </div>
      </motion.div>

      {/* Achievements Section */}
      <motion.div 
        className="py-16 px-4"
        variants={containerVariants}
        initial="hidden"
        whileInView="visible"
        viewport={{ once: true }}
      >
        <div className="max-w-6xl mx-auto">
          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-8">
            {achievements.map((achievement, index) => (
              <motion.div 
                key={index}
                className="bg-white rounded-lg p-6 shadow-sm border border-gray-100"
                variants={itemVariants}
                whileHover={{ y: -5, shadow: "0 10px 25px rgba(0,0,0,0.1)" }}
              >
                <div className="w-12 h-12 bg-blue-100 rounded-lg flex items-center justify-center mb-4">
                  <achievement.icon className="w-6 h-6 text-[#0572AF]" />
                </div>
                <h3 className="font-bold text-gray-900 mb-3 text-sm">
                  {achievement.title}
                </h3>
                <p className="text-gray-600 text-xs leading-relaxed">
                  {achievement.description}
                </p>
              </motion.div>
            ))}
          </div>
        </div>
      </motion.div>

      {/* CTA Section */}
      <motion.div 
        className="bg-[#0572AF] py-16 px-4 text-center"
        variants={containerVariants}
        initial="hidden"
        whileInView="visible"
        viewport={{ once: true }}
      >
        <motion.div 
          className="max-w-4xl mx-auto"
          variants={itemVariants}
        >
          <h2 className="text-3xl md:text-4xl font-bold text-white mb-6">
            Ready to Power Your Critical Systems?
          </h2>
          <p className="text-blue-100 mb-8 text-lg">
            Contact our team for expert advice and technical support
          </p>
          <motion.button 
            className="bg-white text-[#0572AF] px-8 py-3 rounded-lg font-semibold hover:bg-blue-50 transition-colors"
            whileHover={{ scale: 1.05 }}
            whileTap={{ scale: 0.95 }}
            onClick={handleOrderNowClick}
          >
           Order Now
          </motion.button>
        </motion.div>
      </motion.div>
    </div>
  );
};

export default PowerSystemsLanding;